import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'

import { API } from '@/api'
import { GetMachinePriceReq } from '@/api/type-request'
import { GetMachinePriceRes, ServerExtraPrice } from '@/api/type-response'
import { ExtraPriceType } from '@/consts/machine'
import { Image } from '@/models/image'
import { Machine } from '@/models/machine'
import { Snapshot } from '@/models/snapshot'
import {
  buildBaseResourceDesc,
  buildExtraResourceDesc,
  ExtraResourceConfig,
  getAvailableExtraStorageGB,
  normalizeExtraResourceConfig,
} from '@/pages/create-instance/utils/extraStorage'

export type MachinePriceInfo = {
  data?: GetMachinePriceRes
  loading: boolean
  priceAvailable: boolean
  extraStorageGB: number
  availableExtraStorageGB: number
  extraCpuPriceMillicent: number
  extraRamPriceMillicent: number
  extraStoragePriceMillicent: number
  extraResourceDesc?: string
}

const BYTES_PER_GB = 1024 * 1024 * 1024
const DEBOUNCE_MS = 300

function getExtraDiskSizeByte(extraDisk?: ServerExtraPrice) {
  return (
    extraDisk?.extraDiskInfo?.extraSizeByte ?? extraDisk?.Info?.ExtraDiskInfo?.extraSizeByte ?? 0
  )
}

function isExtraCpuPrice(price: ServerExtraPrice) {
  return (
    price.extraType === ExtraPriceType.ExtraCPU ||
    !!price.extraCpuInfo ||
    !!price.Info?.ExtraCPUInfo ||
    !!price.Info?.ExtraCpuInfo
  )
}

function isExtraRamPrice(price: ServerExtraPrice) {
  return (
    price.extraType === ExtraPriceType.ExtraRAM ||
    !!price.extraRamInfo ||
    !!price.Info?.ExtraRAMInfo ||
    !!price.Info?.ExtraRamInfo
  )
}

function isExtraDiskPrice(price: ServerExtraPrice) {
  return (
    price.extraType === ExtraPriceType.ExtraDisk ||
    !!price.extraDiskInfo ||
    !!price.Info?.ExtraDiskInfo
  )
}

export function useMachinePrice({
  machine,
  count,
  checkedImage,
  checkedSnap,
  extraResourceConfig,
}: {
  machine?: Machine
  count: number
  checkedImage?: Image
  checkedSnap?: Snapshot
  extraResourceConfig?: ExtraResourceConfig
}): MachinePriceInfo {
  const request = useMemo<GetMachinePriceReq | undefined>(() => {
    if (!machine || (!checkedImage && !checkedSnap)) return undefined

    const extraResourceDesc = buildExtraResourceDesc(machine, extraResourceConfig)

    return {
      base_resource_desc: buildBaseResourceDesc(machine, count),
      extra_resource_desc: extraResourceDesc,
      img_res_id: checkedSnap ? undefined : checkedImage?.resID,
      snap_res_id: checkedSnap?.resID,
    }
  }, [machine, count, checkedImage, checkedSnap, extraResourceConfig])
  const requestExtraResourceDesc = request?.extra_resource_desc

  const currentKey = useMemo(() => JSON.stringify(request ?? null), [request])
  const [debouncedKey, setDebouncedKey] = useState(currentKey)

  useEffect(() => {
    if (!request) {
      setDebouncedKey(currentKey)
      return
    }

    const timer = setTimeout(() => {
      setDebouncedKey(currentKey)
    }, DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [currentKey, request])

  const debouncedRequest = useMemo<GetMachinePriceReq | undefined>(() => {
    const parsed = JSON.parse(debouncedKey) as GetMachinePriceReq | null
    return parsed ?? undefined
  }, [debouncedKey])

  const query = useQuery({
    queryKey: ['machinePrice', debouncedRequest],
    queryFn: async ({ signal }) => {
      if (!debouncedRequest) return undefined

      return API.getMachinePrice(debouncedRequest, { signal })
    },
    enabled: !!debouncedRequest,
    staleTime: 0,
  })

  const loading = !!request && (currentKey !== debouncedKey || query.isFetching)
  const response = currentKey === debouncedKey && !query.isError ? query.data : undefined
  const isSuccess = response?.code === 0
  const data = isSuccess ? response : undefined
  const priceAvailable = !request ? true : !loading && isSuccess

  const extraPrices = isSuccess ? (response?.extraPrices ?? []) : []
  const extraCpu = extraPrices.find(isExtraCpuPrice)
  const extraRam = extraPrices.find(isExtraRamPrice)
  const extraDisk = extraPrices.find(isExtraDiskPrice)
  const extraSizeByte = getExtraDiskSizeByte(extraDisk)
  const selectedExtraStorageGB =
    normalizeExtraResourceConfig(machine, extraResourceConfig).diskGB ?? 0
  const availableExtraStorageGB = getAvailableExtraStorageGB(machine)
  const extraStorageGB =
    extraSizeByte > 0 ? Math.ceil(extraSizeByte / BYTES_PER_GB) : selectedExtraStorageGB
  const extraCpuPriceMillicent = extraCpu?.priceMillicent ?? 0
  const extraRamPriceMillicent = extraRam?.priceMillicent ?? 0
  const extraStoragePriceMillicent = extraDisk?.priceMillicent ?? 0
  const extraResourceDesc = requestExtraResourceDesc

  return {
    data,
    loading,
    priceAvailable,
    extraStorageGB,
    availableExtraStorageGB,
    extraCpuPriceMillicent,
    extraRamPriceMillicent,
    extraStoragePriceMillicent,
    extraResourceDesc,
  }
}
